/**
 * A world held on a player's own machine.
 *
 * One map, one `Realm`, and everybody who has been let into it - which is
 * exactly what our server is, minus the parts that only make sense on our
 * server. There is no account file here and no post, nobody else is being
 * introduced by it, and nothing it does counts for a rating. What it is
 * *for* is the thing that made the whole port worth doing: the number of
 * worlds people can play in stops being a question about our machine.
 *
 * **The host plays through the same door as everybody else.** They get a
 * `wirePair` to their own world and arrive as a guest like anybody arriving
 * from another city. Not out of neatness: a host with a private path into
 * the world would be a second code path that nothing checks, and it is the
 * path the person most likely to find a bug is standing on.
 *
 * **The clock is not ours.** `beat` is called by whoever is holding this -
 * a page, a check, a script - because a timer is the one thing core must
 * not own. Fifteen a second is what the world expects.
 *
 * Pure: no sockets, no storage, no timers. Give it a `Keep` and wires.
 */
import { Realm } from "./realm.js";
import { route, beat, type Guest } from "./router.js";
import type { Host } from "./host.js";
import type { Wire } from "./link.js";
import type { Keep } from "./keep.js";
import type { GameMap } from "../world/maps.js";
import type { ClientMessage } from "./protocol.js";
import { Budget, MESSAGES_PER_SECOND, MESSAGE_BURST, costOf } from "./limits.js";

export class Held {
  readonly realm: Realm;
  readonly host: Host;
  private readonly guests = new Map<number, Guest>();
  private next = 1;

  constructor(
    /**
     * What this world is called - which is not ours to choose.
     *
     * Our server hands the name out when the world goes on its list, and a
     * stranger coming in names the world they picked off that list. If this
     * called itself something else, every hello would be for a world this
     * page has never heard of. Mirrors `Realm`, deliberately.
     */
    id: string,
    map: GameMap, seed: number, keep: Keep,
    /** The word this world's door asks for, or empty for none. */
    readonly word: string,
    /** Whoever is holding it, by name. */
    readonly owner: string,
  ) {
    this.realm = new Realm(id, map, seed, keep, word, owner);
    this.host = {
      realms: new Map([[this.realm.id, this.realm]]),
      /*
       * A doorman with nothing to do.
       *
       * Counting wrong words per address is our server's business - it is
       * the one with an address anybody can reach. Here, everybody who gets
       * this far was introduced on purpose, and the eight-at-a-time ceiling
       * on that introduction is the limit that matters.
       */
      doorman: { wrongWord: () => false, rightWord: () => { /* nothing */ } },
      /* No names, no post, and nobody else to introduce. */
      names: null,
      post: null,
      board: null,
      reach: (id) => this.guests.get(id) ?? null,
      make: () => ({ ok: false, why: "not-here" }),
      /*
       * Nothing is written down here, and that is the decision.
       *
       * Whatever happens in a world on somebody's own machine is whatever
       * that machine says happened. Keeping a journal of it would look like
       * a record and be a rumour. Ratings come from worlds we host.
       */
      note: () => { /* deliberately nothing */ },
    };
  }

  /** How many are in, for the host to tell the list. */
  get players(): number { return this.realm.players; }

  /**
   * Somebody arriving down a wire.
   *
   * Their id is ours to hand out, not theirs to send: an id is what answers
   * are addressed to, and one anybody could claim would be a way to be sent
   * somebody else's post.
   */
  welcome(wire: Wire, from = "далеко"): Guest {
    const now = Date.now();
    const guest: Guest = {
      ...wire,
      id: this.next++,
      since: 0, realm: null, name: "", skinId: "kozak",
      x: 0, y: 0, z: 0, yaw: 0, pitch: 0,
      lastMoveAt: now, ready: false, flying: false, drone: null,
      held: 0, blows: 0, sent: new Set<string>(),
      from,
      budget: new Budget(MESSAGES_PER_SECOND, MESSAGE_BURST, now),
      strikes: 0, overQueue: 0,
    };
    this.guests.set(guest.id, guest);
    wire.onMessage = (message): void => {
      if (!("t" in (message as { t?: string }))) return;
      const kind = (message as { t: string }).t;
      /*
       * The same budget as our server keeps, for the same reason.
       *
       * Not because a friend floods anybody, but because the far end of
       * this is another browser and nothing about being introduced makes
       * it trustworthy. The person who would be hurt by leaving it out is
       * the one holding the world.
       */
      if (!this.guests.get(guest.id)) return;
      if (!guest.budget.take(Date.now(), costOf(kind))) return;
      try {
        route(this.host, guest, message as ClientMessage);
      } catch {
        // Nothing anybody sends may stop the world. Our server drops a bad
        // message and keeps going; here that matters more, because the
        // process it would stop is somebody's game.
      }
    };
    wire.onClose = (): void => { this.farewell(guest.id); };
    return guest;
  }

  /** They have gone, or been let go of. */
  farewell(id: number): void {
    const guest = this.guests.get(id);
    if (guest === undefined) return;
    this.guests.delete(id);
    guest.realm?.leave(guest);
  }

  /** One beat. Fifteen a second is what the world expects. */
  beat(dt: number): void { beat([this.realm], dt); }

  /** Everybody goes home. */
  stop(): void {
    for (const guest of [...this.guests.values()]) {
      guest.close(1000, "світ зачинено");
      this.farewell(guest.id);
    }
  }
}
